import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {FmlxTextBox} from 'fmlx-common-ui';
import {Button} from 'react-bootstrap';
import NumericInput from '../../../../components/Inputs/NumericInput';
import Spinner from '../../../../components/Spinner/Spinner';
import TaskNumber from '../../Components/TaskNumber';

import {TaskDelayUnit, ExecutionStatus} from '../../../../constants/dataEnum';

import withScrollInput from '../../../../lib/withScrollInput';
import withIntl from '../../../../lib/withIntl';
import {toFmlxOptions} from '../../../../lib/enumHelper';
import {useArrowDownPress, useArrowUpPress, useClickOutside, useEnterPress} from '../../../../lib/customHooks';

import './TaskDelay.scss';
import TaskListCheckbox from '../Components/TaskListCheckbox';
import FastDropdown from '../../../../components/FastDropdown/FastDropdown';

const NumericScrollInput = withScrollInput(NumericInput);

const delayUnitOptions = toFmlxOptions(TaskDelayUnit);

function TaskDelayView({
  ID,
  number,
  task,
  isSelected,
  isIgnored,
  onCheckedChange,
  onEdit,
  onDelete,
  runtimeMode,
  executionStatus,
  isCardMode,
  intl,
  isDragging,
  onSelectTask,
  isHighlightedTask,
  onArrowUpPress,
  onArrowDownPress,
  isViewMode,
}) {
  const taskRef = useRef();
  const onClickOutside = () => {
    if (isHighlightedTask && isCardMode) {
      onSelectTask(ID);
    }
  };

  useArrowUpPress(taskRef, onArrowUpPress);
  useArrowDownPress(taskRef, onArrowDownPress);
  useClickOutside(taskRef, onClickOutside);

  return (
    <div
      ref={taskRef}
      className={`task-delay-view ${(isSelected || isHighlightedTask) && 'selected'} ${isDragging && 'dragging'}`}
      onClick={() => isCardMode && onSelectTask(ID)}
      role="presentation"
    >
      {!isCardMode && <TaskNumber number={number} isHighlighted={isHighlightedTask} />}
      <div className="task-delay-view-content">
        <span className="task-delay-view-icon">
          <img src="./svg/protocol-editor/delay-blue.svg" alt="Delay" />
        </span>
        <span className="task-delay-view-content-delay">
          {intl.formatMessage(
            {
              id: 'protocolEditor.taskList.taskDelay.delayContent',
            },
            {
              delay: `${task.delayTime}`,
              unit: `${task.delayUnit || TaskDelayUnit.SECOND}`,
            },
          )}
        </span>
      </div>
      {!runtimeMode && !isViewMode && (
        <>
          <span
            className={`task-delay-view-edit icon-edit-2 ${isCardMode && 'card-mode'}`}
            onClick={(e) => {
              onEdit(ID);
              onSelectTask(ID);
              e.stopPropagation();
            }}
            role="presentation"
          />
          {!isCardMode && (
            <span
              className="task-delay-view-delete icon-trash"
              onClick={(e) => {
                onDelete(ID);
                e.stopPropagation();
              }}
              role="presentation"
            />
          )}
          {!isCardMode && (
            <TaskListCheckbox
              checked={!isIgnored}
              onChange={(e) => {
                onCheckedChange(ID, e.target.checked);
              }}
            />
          )}
        </>
      )}
      {!!task.message && (
        <div className="task-delay-view-message">
          <span className="title">{intl.formatMessage({id: 'protocolEditor.taskList.taskDelay.messageTitle'})}</span>
          <span className="message break-word">{task.message}</span>
        </div>
      )}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTING && <Spinner />}
      {runtimeMode && executionStatus === ExecutionStatus.EXECUTED && (
        <img className="executed" src="./svg/protocol-editor/execution-done.svg" alt="Executed" />
      )}
    </div>
  );
}

TaskDelayView.propTypes = {
  ID: PropTypes.number.isRequired,
  number: PropTypes.number,
  isSelected: PropTypes.bool.isRequired,
  isIgnored: PropTypes.bool.isRequired,
  onCheckedChange: PropTypes.func.isRequired,
  task: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
  runtimeMode: PropTypes.bool.isRequired,
  executionStatus: PropTypes.string.isRequired,
  isCardMode: PropTypes.bool.isRequired,
  isDragging: PropTypes.bool.isRequired,
  onSelectTask: PropTypes.func.isRequired,
  isHighlightedTask: PropTypes.bool.isRequired,
  onArrowUpPress: PropTypes.func.isRequired,
  onArrowDownPress: PropTypes.func.isRequired,
  isViewMode: PropTypes.bool.isRequired,
};

TaskDelayView.defaultProps = {
  number: undefined,
};

function TaskDelayEditor({initialTask, onApply, excludedComponentRef, intl}) {
  const [task, setTask] = useState(initialTask);

  const delayEditorRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    inputRef.current.focus();
  }, []);

  function doApply() {
    onApply({...task, delayTime: _.isNaN(task.delayTime) ? 0 : parseFloat(task.delayTime)});
  }

  useClickOutside(delayEditorRef, doApply, excludedComponentRef);

  useEnterPress(inputRef, doApply);

  return (
    <div ref={delayEditorRef} className="task-delay-edit" onClick={(e) => e.stopPropagation()} role="presentation">
      <div className="task-delay-edit-title blue">
        {intl.formatMessage({id: 'protocolEditor.taskList.taskDelay.title'})}
      </div>
      <div className="task-delay-edit-input">
        <FastDropdown
          options={delayUnitOptions}
          value={task.delayUnit || TaskDelayUnit.SECOND}
          onSelect={(val) =>
            setTask((prevTask) => ({
              ...prevTask,
              delayUnit: val,
              delayTime: val === TaskDelayUnit.SECOND ? parseInt(task.delayTime, 10) : task.delayTime,
            }))
          }
        />
        <NumericScrollInput
          ref={inputRef}
          value={task.delayTime}
          onChange={(val) =>
            setTask((prevTask) => ({
              ...prevTask,
              delayTime: task.delayUnit === TaskDelayUnit.SECOND ? parseInt(val, 10) || 0 : val,
            }))
          }
          min={0}
          max={9999}
          increment={1}
        />
      </div>
      <div className="task-delay-edit-message">
        <FmlxTextBox
          mode="textarea"
          value={task.message}
          onChange={({value}) => setTask((prevTask) => ({...prevTask, message: value}))}
        />
      </div>
      <div className="task-delay-edit-action">
        <Button bsStyle="success" disabled={!parseFloat(task.delayTime)} onClick={doApply}>
          {intl.formatMessage({id: 'protocolEditor.taskList.taskDelay.apply'})}
        </Button>
      </div>
    </div>
  );
}

TaskDelayEditor.propTypes = {
  initialTask: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  excludedComponentRef: PropTypes.array.isRequired,
  intl: PropTypes.any.isRequired,
};

function TaskDelay({
  ID,
  number,
  initialTask,
  onCheckedChange,
  onApply,
  onDelete,
  runtimeMode,
  executionStatus,
  isCardMode,
  intl,
  drag,
  isDragging,
  onSelectTask,
  onArrowUpPress,
  onArrowDownPress,
  isHighlightedTask,
  deleteModalDelayShowing,
  excludedComponentRef,
  isViewMode,
}) {
  const [editMode, setEditMode] = useState(!runtimeMode && !initialTask.delayTime);

  useEffect(() => {
    setEditMode(!runtimeMode && !initialTask.delayTime);
  }, [initialTask.delayTime, runtimeMode]);

  return (
    <div className="wrapper" ref={(el) => drag && drag(el)}>
      {editMode && (
        <TaskDelayEditor
          ID={ID}
          initialTask={initialTask}
          onApply={(task) => {
            if (deleteModalDelayShowing) {
              return;
            }
            setEditMode(false);
            onApply(task);
          }}
          intl={intl}
          excludedComponentRef={excludedComponentRef}
        />
      )}
      {!editMode && (
        <TaskDelayView
          ID={ID}
          task={initialTask}
          number={number}
          isSelected={!!initialTask.isSelected}
          isIgnored={!!initialTask.isIgnored}
          onCheckedChange={onCheckedChange}
          onEdit={() => setEditMode(true)}
          onDelete={onDelete}
          runtimeMode={runtimeMode}
          executionStatus={executionStatus}
          isCardMode={isCardMode}
          intl={intl}
          isDragging={isDragging}
          onSelectTask={onSelectTask}
          isHighlightedTask={isHighlightedTask}
          onArrowDownPress={onArrowDownPress}
          onArrowUpPress={onArrowUpPress}
          isViewMode={isViewMode}
        />
      )}
    </div>
  );
}

TaskDelay.propTypes = {
  ID: PropTypes.number,
  number: PropTypes.number,
  initialTask: PropTypes.object,
  onCheckedChange: PropTypes.func,
  onApply: PropTypes.func,
  onDelete: PropTypes.func,
  intl: PropTypes.any.isRequired,
  runtimeMode: PropTypes.bool,
  executionStatus: PropTypes.string,
  isCardMode: PropTypes.bool,
  drag: PropTypes.func,
  isDragging: PropTypes.bool,
  onSelectTask: PropTypes.func,
  onArrowUpPress: PropTypes.func,
  onArrowDownPress: PropTypes.func,
  isHighlightedTask: PropTypes.bool,
  deleteModalDelayShowing: PropTypes.bool.isRequired,
  excludedComponentRef: PropTypes.array,
  isViewMode: PropTypes.bool.isRequired,
};

TaskDelay.defaultProps = {
  ID: 0,
  number: undefined,
  initialTask: {
    ID: 0,
    delayTime: 0,
    delayUnit: TaskDelayUnit.SECOND,
    message: '',
  },
  onCheckedChange: (taskId, checked) => {
    console.log('checked change', taskId, checked);
  },
  onApply: (task) => console.log('DelayTask onApply', task),
  onDelete: () => {},
  runtimeMode: false,
  executionStatus: ExecutionStatus.NONE,
  isCardMode: false,
  drag: () => {},
  isDragging: false,
  onSelectTask: () => {},
  onArrowUpPress: () => {},
  onArrowDownPress: () => {},
  isHighlightedTask: false,
  excludedComponentRef: [],
};

function areEqual(prevProps, nextProps) {
  return (
    prevProps.ID === nextProps.ID &&
    _.isEqual(prevProps.initialTask, nextProps.initialTask) &&
    prevProps.runtimeMode === nextProps.runtimeMode &&
    prevProps.executionStatus === nextProps.executionStatus &&
    prevProps.allowDelete === nextProps.allowDelete &&
    prevProps.invalid === nextProps.invalid &&
    prevProps.isHighlightedTask === nextProps.isHighlightedTask
  );
}

export default withIntl(TaskDelay, areEqual);
